import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  CheckSquare,
  Clock,
  CheckCircle,
  Circle,
  Loader,
  Filter,
  Calendar
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TakenOverzicht = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [locaties, setLocaties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 4.10: vandaag in Europe/Amsterdam
  const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
  const [filters, setFilters] = useState({
    datum: searchParams.get('datum') || today,
    status: searchParams.get('status') || '',
    locatie: searchParams.get('locatie') || ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.datum) params.append('datum', filters.datum);
      if (filters.status) params.append('status', filters.status);
      if (filters.locatie) params.append('locatie_id', filters.locatie);
      
      const response = await axios.get(`${API}/management/taken?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching taken:', error);
      toast.error('Fout bij laden taken');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchLocaties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locaties`);
      setLocaties(response.data);
    } catch (error) {
      console.error('Error fetching locaties:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocaties();
  }, [fetchLocaties]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.datum) params.set('datum', newFilters.datum);
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.locatie) params.set('locatie', newFilters.locatie);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ datum: today, status: '', locatie: '' });
    setSearchParams({});
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'klaar':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'bezig':
        return <Loader className="w-4 h-4 text-blue-600" />;
      default:
        return <Circle className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'klaar':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Klaar</Badge>;
      case 'bezig':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Bezig</Badge>;
      default:
        return <Badge variant="secondary" className="bg-slate-100 text-slate-800">Open</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  const dateStr = data?.datum ? new Date(data.datum).toLocaleDateString('nl-NL', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  }) : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <CheckSquare className="w-7 h-7" />
            Taken Overzicht
          </h1>
          <p className="text-slate-500">{dateStr}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-800">{data?.stats?.totaal || 0}</div>
            <div className="text-sm text-slate-500">Totaal</div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{data?.stats?.klaar || 0}</div>
            <div className="text-sm text-slate-500">Klaar</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{data?.stats?.bezig || 0}</div>
            <div className="text-sm text-slate-500">Bezig</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-600">{data?.stats?.open || 0}</div>
            <div className="text-sm text-slate-500">Open</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{data?.stats?.progress || 0}%</div>
            <div className="text-sm text-slate-500">Voortgang</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <Input
                type="date"
                value={filters.datum}
                onChange={(e) => handleFilterChange('datum', e.target.value)}
                className="w-40"
              />
            </div>
            
            <Select value={filters.status} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="bezig">Bezig</SelectItem>
                <SelectItem value="klaar">Klaar</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.locatie} onValueChange={(v) => handleFilterChange('locatie', v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Locatie" />
              </SelectTrigger>
              <SelectContent>
                {locaties.map(loc => (
                  <SelectItem key={loc._id} value={loc._id}>{loc.naam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {(filters.status || filters.locatie || filters.datum !== today) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Wis filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Locations with tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Taken per locatie ({data?.locaties?.length || 0} locaties)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!data?.locaties || data.locaties.length === 0) ? (
            <div className="text-center py-8 text-slate-500">
              <CheckSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Geen taken gevonden voor deze datum</p>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-2">
              {data.locaties.map((loc) => (
                <AccordionItem key={loc.locatieId} value={loc.locatieId} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-32 text-left">
                        <span className="font-medium text-slate-800">{loc.locatieNaam}</span>
                        <span className="text-sm text-slate-500 ml-2">({loc.locatieType})</span>
                      </div>
                      <div className="flex-1 max-w-xs">
                        <Progress value={loc.progress} className="h-2" />
                      </div>
                      <div className="w-24 text-right">
                        <span className={`font-medium ${loc.progress === 100 ? 'text-green-600' : 'text-slate-800'}`}>
                          {loc.klaar}/{loc.totaal}
                        </span>
                        <span className="text-slate-500 ml-1">({loc.progress}%)</span>
                      </div>
                      {loc.progress === 100 && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">Klaar</Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2">
                      {loc.taken.map((taak) => (
                        <div key={taak.id} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-lg">
                          {getStatusIcon(taak.status)}
                          <span className="flex-1 text-slate-700">{taak.titel}</span>
                          {taak.tijd && (
                            <span className="text-sm text-slate-500">{taak.tijd}</span>
                          )}
                          {getStatusBadge(taak.status)}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TakenOverzicht;
